import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import Login from "./pages/Login";
import Register from "./pages/Register";
import CompleteRegistration from "./pages/CompleteRegistration";
import CreateCompanyProfile from "./pages/onboarding/CreateCompanyProfile";
import SelectCompanyPlan from "./pages/onboarding/SelectCompanyPlan";
import Dashboard from "./pages/Dashboard";
import ReferralRedirect from "./pages/ReferralRedirect";
import Profile from "./pages/Profile";
import EmployeeList from "./pages/employees/EmployeeList";
import EmployeeDocuments from "./pages/employees/EmployeeDocuments";
import AddEmployee from "./pages/employees/AddEmployee";
import EditEmployee from "./pages/employees/EditEmployee";
import SalaryList from "./pages/salary/SalaryList";
import AddSalary from "./pages/salary/AddSalary";
import EditSalary from "./pages/salary/EditSalary";
import DeductionsList from "./pages/deductions/DeductionsList";
import MarkAttendance from "./pages/attendance/MarkAttendance";
import ViewAttendance from "./pages/attendance/ViewAttendance";
import GeneratePayroll from "./pages/payroll/GeneratePayroll";
import ProcessPayroll from "./pages/payroll/ProcessPayroll";
import ViewPayroll from "./pages/payroll/ViewPayroll";
import PayrollDetails from "./pages/payroll/PayrollDetails";
import LeaveMaster from "./pages/leave/LeaveMaster";
import WeeklyHolidays from "./pages/holidays/WeeklyHolidays";
import AdHocHolidays from "./pages/holidays/AdHocHolidays";
import LeaveApplications from "./pages/leave/LeaveApplications";
import LeaveBalance from "./pages/leave/LeaveBalance";
import SalaryAdvances from "./pages/salary/SalaryAdvances";
import Reimbursements from "./pages/reimbursements/Reimbursements";
import ChangePassword from "./pages/profile/ChangePassword";
import AttendanceSettings from "./pages/attendance/AttendanceSettings";
import SalaryAdvanceSettings from "./pages/salary/SalaryAdvanceSettings";
import Tickets from "./pages/tickets/Tickets";
import TicketView from "./pages/tickets/TicketView";
import ArchivedEmployees from "./pages/employees/ArchivedEmployees";
import Notifications from "./pages/notifications/Notifications";
import PageLoader from "./components/common/PageLoader";
import AllowanceSettings from "./pages/deductions/AllowanceSettings";
import RolesPermissions from "./pages/settings/RolesPermissions";
import UpgradePlans from "./pages/plans/UpgradePlans";
import UpgradePlanDetails from "./pages/plans/UpgradePlanDetails";
import PaymentHistory from "./pages/plans/PaymentHistory";
import PlanPricingSettings from "./pages/plans/PlanPricingSettings";
// import UsersPage from './pages/UsersPage';

// Protected Route Component
const ProtectedRoute = ({ children, permission }) => {
  const { isAuthenticated, loading, hasPermission, onboardingPending, onboardingPath } = useAuth();

  if (loading) {
    return <PageLoader force copy="Preparing your workspace" />;
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (onboardingPending && window.location.pathname !== onboardingPath) {
    return <Navigate to={onboardingPath || "/complete-registration"} replace />;
  }

  if (permission && !hasPermission(permission)) {
    return <Navigate to="/dashboard" replace />;
  }

  return children;
};

// Public Route Component (redirect to dashboard if already logged in)
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading, onboardingPending, onboardingPath } = useAuth();

  if (loading) {
    return <PageLoader force copy="Checking your session" />;
  }

  if (!isAuthenticated) {
    return children;
  }

  return <Navigate to={onboardingPending ? (onboardingPath || "/complete-registration") : "/dashboard"} replace />;
};

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <PageLoader />
        <Routes>
          {/* Referral tracking route */}
          <Route path="/ref/:token" element={<ReferralRedirect />} />
          {/* Public Routes */}
          <Route
            path="/login"
            element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            }
          />
          <Route
            path="/register"
            element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            }
          />
          <Route
            path="/complete-registration"
            element={
              <ProtectedRoute>
                <CompleteRegistration />
              </ProtectedRoute>
            }
          />
          <Route
            path="/create-company-profile"
            element={
              <ProtectedRoute>
                <CreateCompanyProfile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/select-company-plan"
            element={
              <ProtectedRoute>
                <SelectCompanyPlan />
              </ProtectedRoute>
            }
          />
          {/* Google OAuth Callback */}
          <Route path="/auth/google/callback" element={<Login />} />
          {/* Protected Routes */}
          <Route
            path="/dashboard"
            element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            }
          />
          <Route
            path="/people/list"
            element={
              <ProtectedRoute permission="people.view">
                <EmployeeList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/people/documents"
            element={
              <ProtectedRoute permission="profile.view">
                <EmployeeDocuments />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/add"
            element={
              <ProtectedRoute permission="people.manage">
                <AddEmployee />
              </ProtectedRoute>
            }
          />
          <Route
            path="/employees/edit/:id"
            element={
              <ProtectedRoute permission="people.manage">
                <EditEmployee />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute permission="profile.view">
                <Profile />
              </ProtectedRoute>
            }
          />
          <Route
            path="/upgrade"
            element={
              <ProtectedRoute permission="profile.view">
                <UpgradePlans />
              </ProtectedRoute>
            }
          />
          <Route
            path="/upgrade-plan/:id"
            element={
              <ProtectedRoute permission="profile.view">
                <UpgradePlanDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/plan-history"
            element={
              <ProtectedRoute permission="profile.view">
                <PaymentHistory />
              </ProtectedRoute>
            }
          />
          <Route
            path="/super-admin/plan-pricing"
            element={
              <ProtectedRoute>
                <PlanPricingSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/salary"
            element={
              <ProtectedRoute permission="salary.view">
                <SalaryList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/deductions"
            element={
              <ProtectedRoute permission="tax_declarations.view">
                <DeductionsList />
              </ProtectedRoute>
            }
          />
          <Route
            path="/allowance-settings"
            element={
              <ProtectedRoute permission="benefits.view">
                <AllowanceSettings />
              </ProtectedRoute>
            }
          />
          <Route path="/benefits-settings" element={<Navigate to="/allowance-settings" replace />} />
          <Route
            path="/salary/add/:userId"
            element={
              <ProtectedRoute permission="salary.manage">
                <AddSalary />
              </ProtectedRoute>
            }
          />
          <Route
            path="/salary/edit/:userId"
            element={
              <ProtectedRoute permission="salary.manage">
                <EditSalary />
              </ProtectedRoute>
            }
          />
          <Route  
            path="/attendance/mark"
            element={
              <ProtectedRoute permission="attendance.mark">
                <MarkAttendance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance/view"
            element={
              <ProtectedRoute permission="attendance.view">
                <ViewAttendance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll/generate"
            element={
              <ProtectedRoute permission="payroll.generate">
                <GeneratePayroll />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll/process"
            element={
              <ProtectedRoute permission="payroll.generate">
                <ProcessPayroll />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll/list"
            element={
              <ProtectedRoute permission="payroll.view">
                <ViewPayroll />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll/details"
            element={
              <ProtectedRoute permission="payroll.view">
                <PayrollDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/payroll/details/:payrollId"
            element={
              <ProtectedRoute permission="payroll.view">
                <PayrollDetails />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave/master"
            element={
              <ProtectedRoute permission="leave.types.manage">
                <LeaveMaster />
              </ProtectedRoute>
            }
          />
          <Route
            path="/holidays/weekly"
            element={
              <ProtectedRoute permission="weekly_holidays.manage">
                <WeeklyHolidays />
              </ProtectedRoute>
            }
          />
          <Route
            path="/holidays/ad-hoc"
            element={
              <ProtectedRoute permission="ad_hoc_holidays.manage">
                <AdHocHolidays />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave/applications"
            element={
              <ProtectedRoute permission="leave.applications.view">
                <LeaveApplications />
              </ProtectedRoute>
            }
          />
          <Route
            path="/leave/balance"
            element={
              <ProtectedRoute permission="leave.balance.view">
                <LeaveBalance />
              </ProtectedRoute>
            }
          />
          <Route
            path="/salary/advances"
            element={
              <ProtectedRoute permission="salary_advances.view">
                <SalaryAdvances />
              </ProtectedRoute>
            }
          />
          <Route
            path="/reimbursements"
            element={
              <ProtectedRoute permission="reimbursements.view">
                <Reimbursements />
              </ProtectedRoute>
            }
          />
          <Route
            path="/change-password"
            element={
              <ProtectedRoute permission="change_password.view">
                <ChangePassword />
              </ProtectedRoute>
            }
          />
          <Route
            path="/attendance/settings"
            element={
              <ProtectedRoute permission="attendance.settings.manage">
                <AttendanceSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/salary/advance-settings"
            element={
              <ProtectedRoute permission="salary_advance_settings.manage">
                <SalaryAdvanceSettings />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets"
            element={
              <ProtectedRoute permission="tickets.view">
                <Tickets />
              </ProtectedRoute>
            }
          />
          <Route
            path="/tickets/:id"
            element={
              <ProtectedRoute permission="tickets.view">
                <TicketView />
              </ProtectedRoute>
            }
          />
          <Route
            path="/archived-employees"
            element={
              <ProtectedRoute permission="archives.view">
                <ArchivedEmployees />
              </ProtectedRoute>
            }
          />
          <Route
            path="/roles-permissions"
            element={
              <ProtectedRoute permission="roles_permissions.manage">
                <RolesPermissions />
              </ProtectedRoute>
            }
          />
          <Route
            path="/notifications"
            element={
              <ProtectedRoute permission="notifications.view">
                <Notifications />
              </ProtectedRoute>
            }
          />
          {/* Default Route */}
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          {/* 404 Route */}
          <Route path="*" element={<Navigate to="/dashboard" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
